import { ArrowRight, CheckCircle, Calendar, Home, DollarSign, Clock } from 'lucide-react';
import CTAButton from '../components/CTAButton';

export default function HowItWorksPage() {
  return (
    <div className="min-h-screen">
      <section className="pt-12 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">How It Works</h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Our process is straightforward and designed around your timeline. Here's exactly what happens from start to finish.
          </p>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-12">
            {[
              {
                step: '1',
                title: 'Tell Us About Your Property',
                icon: Home,
                details: [
                  'Fill out a quick form with your property address and basic details',
                  'Tell us about the property condition (no need to over-explain—we buy as-is)',
                  'Let us know your situation and timeline',
                  'Everything is kept completely private and confidential'
                ],
                cta: true
              },
              {
                step: '2',
                title: 'We Review & Prepare an Offer',
                icon: DollarSign,
                details: [
                  'Our team reviews your property information',
                  'We analyze the local market and property condition',
                  'We prepare a fair cash offer based on the property value',
                  "You'll hear back from us typically within 24-48 hours",
                  "There's no obligation to accept—you can say no and we won't bother you again"
                ]
              },
              {
                step: '3',
                title: 'Quick Walkthrough (On Your Time)',
                icon: Clock,
                details: [
                  "If you're interested, we schedule a brief walkthrough at your convenience",
                  'One of our team members visits to see the property firsthand',
                  'We answer any questions you have',
                  'This is informal—no pressure, no sales pitch',
                  'Still no obligation. You can accept the offer as-is or negotiate'
                ]
              },
              {
                step: '4',
                title: 'You Choose Your Closing Date',
                icon: Calendar,
                details: [
                  'Once we agree on terms, you decide when you want to close',
                  'We can typically close in 7 days or as long as you need',
                  'We handle all the paperwork and coordinate with the title company',
                  'There are no financing contingencies or inspections to worry about',
                  "We cover all closing costs—you don't pay a dime"
                ]
              },
              {
                step: '5',
                title: 'Close & Get Paid',
                icon: CheckCircle,
                details: [
                  'You meet with the title company to sign closing documents',
                  'You receive your cash payment on closing day',
                  "The property is transferred, and you're done",
                  'No commissions. No hidden fees. Just a clean transaction'
                ]
              }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="flex gap-8">
                  <div className="flex flex-col items-center">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-900 to-blue-950 flex items-center justify-center text-white text-3xl font-bold mb-4 flex-shrink-0">
                      {item.step}
                    </div>
                    {index < 4 && (
                      <div className="w-1 h-24 bg-blue-300 my-4"></div>
                    )}
                  </div>
                  <div className="pb-12 flex-1">
                    <div className="flex items-start gap-4 mb-4">
                      <item.icon className="w-8 h-8 text-blue-900 mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="text-3xl font-bold text-gray-900">{item.title}</h3>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-100 p-6 shadow-sm">
                      <ul className="space-y-3">
                        {item.details.map((detail, idx) => (
                          <li key={idx} className="flex gap-3">
                            <ArrowRight className="w-5 h-5 text-blue-900 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{detail}</span>
                          </li>
                        ))}
                      </ul>
                      {item.cta && (
                        <div className="mt-6 pt-6 border-t">
                          <CTAButton text="Start Here" size="md" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-blue-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">Important Things to Know</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">No Obligation</h3>
              <p className="text-gray-600 leading-relaxed">
                Requesting an offer is completely free and doesn&apos;t commit you to anything. You can say no at any stage, and we won&apos;t follow up or bother you.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">As-Is Purchase</h3>
              <p className="text-gray-600 leading-relaxed">
                We buy your home in its current condition. No repairs needed. No inspections required. No appraisals.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Your Timeline</h3>
              <p className="text-gray-600 leading-relaxed">
                We can typically close within 7 days if you need to, or we can wait weeks if that works better for your situation.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">We Handle Everything</h3>
              <p className="text-gray-600 leading-relaxed">
                Title work, inspections, appraisals, closing costs—we take care of it all. You don't pay anything.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Privacy Guaranteed</h3>
              <p className="text-gray-600 leading-relaxed">
                Your information is completely confidential. We won&apos;t share it or use it to contact you if you&apos;re not interested.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Cash Payment</h3>
              <p className="text-gray-600 leading-relaxed">
                You get paid in cash at closing. No waiting for financing to fall through or deals to fall apart.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">Frequently Asked Questions About Our Process</h2>
          <div className="space-y-6">
            {[
              {
                q: 'How do you calculate your offer?',
                a: 'We look at the local market, comparable sales, the property condition, and any needed repairs. Our goal is to make a fair offer that works for both of us.'
              },
              {
                q: 'Can I still negotiate after receiving an offer?',
                a: 'Yes. If you think the offer is low, we can discuss it. We might be able to adjust based on new information or a follow-up walkthrough.'
              },
              {
                q: 'What if I need to close quickly?',
                a: "We can close in as little as 7 days. Just let us know your timeline and we'll make it happen."
              },
              {
                q: 'What if I need more time?',
                a: 'No problem. We can work with your schedule. Whether you need 30 days, 60 days, or longer, we can accommodate.'
              },
              {
                q: 'Do I have to accept the first offer?',
                a: "No. You can counter our offer, ask questions, or turn it down entirely. It's your decision."
              },
              {
                q: 'Who pays for closing costs?',
                a: "We do. We cover all title company fees, closing costs, and other transaction expenses. You don't pay anything."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm">
                <h4 className="text-lg font-bold text-gray-900 mb-2">{faq.q}</h4>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-900 to-blue-950">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl mb-8 text-blue-50">
            Getting your offer takes just a few minutes. No obligation, no pressure.
          </p>
          <CTAButton text="Get Your Offer" size="lg" variant="primary" />
        </div>
      </section>
    </div>
  );
}
